import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Collapse,
  IconButton,
  Chip,
  Grid,
} from "@material-ui/core";
import {
  ExpandLess,
  ExpandMore,
  CheckCircle,
  Error,
  CloudDownload,
  Close,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { green, red, blue, grey } from "@material-ui/core/colors";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    position: "relative",
  },
  rootImporting: {
    border: `2px solid ${blue[500]}`,
    boxShadow: `0 0 10px ${blue[200]}`,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  icon: {
    fontSize: 24,
  },
  iconImporting: {
    color: blue[500],
  },
  iconSuccess: {
    color: green[500],
  },
  iconError: {
    color: red[500],
  },
  progressBar: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    height: 8,
    borderRadius: 4,
  },
  stats: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
  chip: {
    fontWeight: 500,
  },
  timestamp: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    marginTop: theme.spacing(1),
  },
  closeButton: {
    padding: theme.spacing(0.5),
  },
}));

const MessageImportMonitor = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const companyId = user?.companyId;

  const [importProgress, setImportProgress] = useState(null);
  const [syncProgress, setSyncProgress] = useState(null); // progresso do WhatsApp (0-100)
  const [accumulated, setAccumulated] = useState(0); // mensagens acumuladas do WhatsApp
  const [processingStarted, setProcessingStarted] = useState(false); // já iniciou processamento
  const [isExpanded, setIsExpanded] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [lastEventTime, setLastEventTime] = useState(null);
  const [estimatedTime, setEstimatedTime] = useState(null);

  useEffect(() => {
    if (!socket || !companyId) return;

    const eventName = `importMessages-${companyId}`;

    const handleProgress = (data) => {
      if (data.action === "refresh") {
        setImportProgress(null);
        setSyncProgress(null);
        setAccumulated(0);
        setProcessingStarted(false);
        setIsVisible(false);
        return;
      }

      if (data.action !== "update" || !data.status) return;

      const status = data.status;
      const now = Date.now();

      // Fase de recebimento do WhatsApp (this === -1)
      // Ignora se já está processando (evita regressão de "Importando" → "Preparando")
      if (status.this === -1) {
        if (processingStarted) return;
        if (status.progress != null) setSyncProgress(status.progress);
        if (status.accumulated != null) setAccumulated(status.accumulated);
        setIsVisible(true);
        setImportProgress(status);
        return;
      }

      // Fase de processamento (this >= 0)
      if (!processingStarted) setProcessingStarted(true);
      if (lastEventTime && status.this > 0 && !status.completed) {
        const timeSinceLastEvent = (now - lastEventTime) / 1000;
        const remaining = status.all - status.this;
        if (remaining > 0 && timeSinceLastEvent > 0) {
          const estimatedSeconds = (remaining / (status.this || 1)) * timeSinceLastEvent;
          setEstimatedTime(Math.min(estimatedSeconds, 3600));
        }
      }

      setLastEventTime(now);
      setImportProgress(status);
      setIsVisible(true);

      if (status.completed) {
        setEstimatedTime(null);
        setSyncProgress(null);
        setAccumulated(0);
        setProcessingStarted(false);
      }
    };

    socket.on(eventName, handleProgress);

    return () => {
      socket.off(eventName, handleProgress);
    };
  }, [socket, companyId, lastEventTime, processingStarted]);

  if (!isVisible || !importProgress) {
    return null;
  }

  const handleClose = () => {
    setIsVisible(false);
    setImportProgress(null);
    setEstimatedTime(null);
    setLastEventTime(null);
    setSyncProgress(null);
    setAccumulated(0);
    setProcessingStarted(false);
  };

  const percentage = importProgress.all > 0
    ? Math.round((importProgress.this / importProgress.all) * 100)
    : 0;

  const formatEstimatedTime = (seconds) => {
    if (!seconds || seconds <= 0) return null;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    if (minutes > 0) {
      return `${minutes}min ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  const isReceiving = importProgress.this === -1 && accumulated > 0; // recebendo do WhatsApp
  const isPreparing = importProgress.this === -1 && accumulated === 0; // job criado, aguardando processamento
  const isImporting = !importProgress.completed && importProgress.this >= 0;
  const isCompleted = !!importProgress.completed;
  const hasErrors = importProgress.errors > 0;

  return (
    <Paper
      className={`${classes.root} ${(isImporting || isPreparing || isReceiving) ? classes.rootImporting : ""}`}
      elevation={3}
    >
      <Box className={classes.header}>
        <Box className={classes.title}>
          {isCompleted ? (
            <CheckCircle className={`${classes.icon} ${classes.iconSuccess}`} />
          ) : (
            <CloudDownload className={`${classes.icon} ${classes.iconImporting}`} />
          )}
          <Typography variant="h6">
            {isCompleted
              ? "Importacao de mensagens concluida"
              : isReceiving
              ? "Recebendo historico do WhatsApp"
              : isPreparing
              ? "Preparando importacao de mensagens..."
              : "Importando mensagens do historico"}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center">
          <IconButton
            size="small"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
          {isCompleted && (
            <IconButton
              size="small"
              className={classes.closeButton}
              onClick={handleClose}
            >
              <Close fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      <Collapse in={isExpanded}>
        <Box>
          {isReceiving ? (
            <>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">
                  {accumulated}
                  {syncProgress > 0
                    ? ` / ~${Math.round(accumulated / (syncProgress / 100))} mensagens recebidas`
                    : " mensagens recebidas"}
                </Typography>
                {syncProgress != null && (
                  <Typography variant="body2" color="primary">
                    <strong>{syncProgress}%</strong>
                  </Typography>
                )}
              </Box>
              <LinearProgress
                variant={syncProgress != null ? "determinate" : "indeterminate"}
                value={syncProgress || 0}
                className={classes.progressBar}
                color="secondary"
              />
            </>
          ) : isPreparing ? (
            <>
              <Typography variant="body2" color="textSecondary">
                Aguarde, preparando mensagens para importacao...
              </Typography>
              <LinearProgress
                variant="indeterminate"
                className={classes.progressBar}
                color="secondary"
              />
            </>
          ) : (
            <>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="body2">
                  {importProgress.this} / {importProgress.all} mensagens
                </Typography>
                <Typography variant="body2" color="primary">
                  <strong>{percentage}%</strong>
                </Typography>
              </Box>

              {estimatedTime && !isCompleted && (
                <Typography variant="caption" color="primary" style={{ fontStyle: "italic" }}>
                  Tempo estimado: ~{formatEstimatedTime(estimatedTime)}
                </Typography>
              )}

              <LinearProgress
                variant="determinate"
                value={percentage}
                className={classes.progressBar}
                color={isCompleted ? "primary" : "secondary"}
              />
            </>
          )}

          {isImporting && <Grid container spacing={1} className={classes.stats}>
            {importProgress.created > 0 && (
              <Grid item>
                <Chip
                  label={`Criadas: ${importProgress.created}`}
                  size="small"
                  className={classes.chip}
                  style={{ backgroundColor: green[100], color: green[800] }}
                />
              </Grid>
            )}
            {hasErrors && (
              <Grid item>
                <Chip
                  label={`Erros: ${importProgress.errors}`}
                  size="small"
                  className={classes.chip}
                  style={{ backgroundColor: red[100], color: red[800] }}
                />
              </Grid>
            )}
            {importProgress.this > 0 && (
              <Grid item>
                <Chip
                  label={`Duplicadas: ${importProgress.this - (importProgress.created || 0) - (importProgress.errors || 0)}`}
                  size="small"
                  className={classes.chip}
                  style={{ backgroundColor: grey[300], color: grey[800] }}
                />
              </Grid>
            )}
          </Grid>}

          {isCompleted && (
            <Typography
              variant="body2"
              style={{ color: green[700], marginTop: 8, fontWeight: 500 }}
            >
              Importacao finalizada com sucesso!
            </Typography>
          )}

          {importProgress.timestamp && (
            <Typography className={classes.timestamp}>
              {new Date(importProgress.timestamp).toLocaleString()}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default MessageImportMonitor;
